import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import Textarea from '@/components/basicComponents/textarea';
import { useErrorMessages } from '@/hooks/i18n';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useFieldEditorContext } from '../../context/FieldEditorContext';
import FieldEditorContentWrapper from '../FieldEditorContentWrapper';

const ShortTextContent = () => {
  const { default: defaultErrorMessage } = useErrorMessages();
  const { currentField, shortTextAdditionalProperties, handleUpdateFieldAnswer, mainQueryRefetch, handleClose } = useFieldEditorContext();
  const { control, reset, watch, setValue, handleSubmit } = useForm({ defaultValues: { fieldValue: '' } });

  const [isEditMode, setIsEditMode] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<{ title: string; message: string } | null>(null);

  useEffect(() => {
    if (!currentField) return;

    if (currentField?.value === null && currentField.AI?.answers.length) {
      setIsEditMode(false);
      reset({ fieldValue: currentField.AI.answers[0] });
    } else {
      setIsEditMode(true);
      reset({ fieldValue: currentField?.value || '' });
    }
  }, [currentField]);

  const saveValue = async (data: { fieldValue: string }) => {
    try {
      setError(null);
      setLoading(true);

      await handleUpdateFieldAnswer({
        id: currentField.fieldAnswerId,
        recordId: currentField.recordId,
        value: isEditMode ? data.fieldValue : null
      });

      await mainQueryRefetch();
      handleClose();
    } catch (error) {
      setError({ title: 'Error', message: defaultErrorMessage });
    } finally {
      setLoading(false);
    }
  };

  const editSuggested = (suggestion: string) => {
    setValue('fieldValue', suggestion);
    setIsEditMode(true);
  };

  const clearSuggestion = () => {
    setValue('fieldValue', currentField?.AI?.answers?.length ? '' : null);
    setIsEditMode(true);
  };

  const startWithAISuggestion = () => {
    setValue('fieldValue', currentField.AI?.answers[0]);
    setIsEditMode(false);
  };

  const revertPriorDraft = () => {
    if (currentField?.value) {
      setValue('fieldValue', currentField.value);
      setIsEditMode(true);
    }
  };

  if (!currentField) return <></>;

  return (
    <>
      {loading && (
        <ModalWrappedContent isOpen={loading} title="Saving" showCancelButton={false} showCloseIcon={false} onClose={() => {}}>
          <ModalLoader />
        </ModalWrappedContent>
      )}
      <FieldEditorContentWrapper
        isEditMode={isEditMode}
        showRevertPriorDraft={Boolean(currentField?.value) && currentField?.value !== watch('fieldValue')}
        error={error}
        confirmButton={{ name: 'Save', onClick: handleSubmit(saveValue) }}
        startWithAISuggestion={startWithAISuggestion}
        editSuggested={editSuggested}
        clearSuggestion={clearSuggestion}
        revertPriorDraft={revertPriorDraft}
      >
        <Controller
          name="fieldValue"
          control={control}
          rules={{
            validate: (value) =>
              !isEditMode || !value || value?.length <= (shortTextAdditionalProperties?.maxLength || 250) || 'Maximum number of characters exceeded'
          }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Textarea
              value={isEditMode ? value || '' : currentField.AI?.answers[0]}
              onChange={(e) => onChange(e.target.value)}
              label={currentField.name}
              styleType={isEditMode ? 'default' : 'magicWand'}
              size="md"
              errorText={error?.message}
              limitCharactersTo={isEditMode ? shortTextAdditionalProperties?.maxLength || 250 : 0}
              autoFocus
            />
          )}
        />
      </FieldEditorContentWrapper>
    </>
  );
};

export default ShortTextContent;
